import {graphql} from 'react-relay';
import type {Environment, GraphQLTaggedNode, MutationParameters, PayloadError, RecordSourceSelectorProxy} from 'relay-runtime';
import Util from '../../util/util';
import {trackAndCommitMutation} from '../../../../tracking/amplitude/TrackMutation';
import type {ScheduleDayOfMonth, ScheduleFrequency, ScheduleWeekday} from './scheduleOptions';

export type ScheduleSnapshot = {
	id: string;
	scheduleId: string;
	frequency: ScheduleFrequency;
	dayOfMonth: ScheduleDayOfMonth | null;
	weekdays: ReadonlyArray<ScheduleWeekday>;
	timeOfDay: string;
	timezone: string;
};
export type ScheduleSetSnapshot = {id: string; consumer: string; enabled: boolean; schedules: ReadonlyArray<ScheduleSnapshot>};
export type ScheduleChange = Partial<
	Pick<ScheduleSnapshot, 'frequency' | 'dayOfMonth' | 'weekdays' | 'timeOfDay' | 'timezone'>
>;
export type NewScheduleFields = Omit<ScheduleSnapshot, 'id' | 'scheduleId'>;
export type ScheduleWriteFailure = {kind: 'refused'} | {kind: 'conflict'} | {kind: 'notFound'};
export type ScheduleWriteCallbacks = {onRefused: (failure: ScheduleWriteFailure) => void; onSettled: () => void};

// Every mutation answers with the whole set, selected field by field (no fragment spreads) so the
// optimistic responses below can match the selection exactly.
const createMutation = graphql`
	mutation scheduleMutationsCreateMutation($input: CreateScheduleInput!) {
		createSchedule(input: $input) {
			scheduleSet {
				id
				consumer
				enabled
				schedules {
					id
					scheduleId
					frequency
					dayOfMonth
					weekdays
					timeOfDay
					timezone
				}
			}
		}
	}
`;

const updateMutation = graphql`
	mutation scheduleMutationsUpdateMutation($input: UpdateScheduleInput!) {
		updateSchedule(input: $input) {
			scheduleSet {
				id
				consumer
				enabled
				schedules {
					id
					scheduleId
					frequency
					dayOfMonth
					weekdays
					timeOfDay
					timezone
				}
			}
		}
	}
`;

const deleteMutation = graphql`
	mutation scheduleMutationsDeleteMutation($input: DeleteScheduleInput!) {
		deleteSchedule(input: $input) {
			scheduleSet {
				id
				consumer
				enabled
				schedules {
					id
					scheduleId
					frequency
					dayOfMonth
					weekdays
					timeOfDay
					timezone
				}
			}
		}
	}
`;

const setEnabledMutation = graphql`
	mutation scheduleMutationsSetEnabledMutation($input: SetScheduleSetEnabledInput!) {
		setScheduleSetEnabled(input: $input) {
			scheduleSet {
				id
				consumer
				enabled
				schedules {
					id
					scheduleId
					frequency
					dayOfMonth
					weekdays
					timeOfDay
					timezone
				}
			}
		}
	}
`;

// The {commit} class shape Util.CommitMutation drives. The optimistic updater touches only the record the
// write changes, so overlapping writes on different rows compose, and Relay drops the updater's layer if the
// write fails, which puts every control back to the stored value.
const optimisticMutation = (mutation: GraphQLTaggedNode, optimisticUpdater: (store: RecordSourceSelectorProxy) => void) => ({
	commit(
		environment: Environment,
		input: Record<string, unknown>,
		onSuccess: (response: unknown, errors: readonly PayloadError[] | null | undefined) => void,
		onError: (error: Error) => void
	) {
		return trackAndCommitMutation<MutationParameters>(environment, {
			mutation,
			variables: {input: {csrfToken: Util.getCsrfValue(), socketClientId: Util.getClientId(), ...input}},
			optimisticUpdater,
			onCompleted: onSuccess,
			onError,
		} as never);
	},
});

// A Relay failure carries the GraphQL errors on `res.errors`. Its own message is Relay boilerplate.
const firstError = (error: unknown): {message?: unknown} | null => {
	const errors = ((error as {res?: {errors?: unknown}} | null)?.res as {errors?: unknown} | undefined)?.errors;
	return Array.isArray(errors) && errors.length > 0 ? errors[0] : null;
};

export const classifyScheduleFailure = (error: unknown): ScheduleWriteFailure | null => {
	const first = firstError(error);
	const message = typeof first?.message === 'string' ? first.message : '';
	if (message.startsWith('SCHEDULE_REFUSED:')) {
		return {kind: 'refused'};
	}
	if (message.startsWith('SCHEDULE_CONFLICT:')) {
		return {kind: 'conflict'};
	}
	if (message.startsWith('404:')) {
		return {kind: 'notFound'};
	}
	return null;
};

const commit = (
	mutationClass: ReturnType<typeof optimisticMutation>,
	input: Record<string, unknown>,
	callbacks: ScheduleWriteCallbacks & {onSucceeded?: () => void},
	retry: boolean
): void => {
	Util.CommitMutation(
		mutationClass,
		input,
		() => {
			callbacks.onSucceeded?.();
			callbacks.onSettled();
		},
		retry,
		null,
		(transaction: unknown, onFailure: (t: unknown) => boolean | undefined) => {
			const failure = classifyScheduleFailure(transaction);
			if (failure) {
				callbacks.onRefused(failure);
				callbacks.onSettled();
				return;
			}
			// Util.CommitMutation answers true when it has already scheduled a retry of a transient failure;
			// the write is not finished until that retry settles.
			if (onFailure(transaction) !== true) {
				callbacks.onSettled();
			}
		}
	);
};

let optimisticCounter = 0;

export const commitSetEnabled = (
	set: ScheduleSetSnapshot,
	enabled: boolean,
	callbacks: ScheduleWriteCallbacks & {onSucceeded: () => void}
): void =>
	commit(
		optimisticMutation(setEnabledMutation, store => {
			store.get(set.id)?.setValue(enabled, 'enabled');
		}),
		{consumer: set.consumer, enabled},
		callbacks,
		true
	);

// Not retried: a create whose response was lost may already have happened, and a retry would make two.
export const commitCreateSchedule = (
	set: ScheduleSetSnapshot,
	fields: NewScheduleFields,
	callbacks: ScheduleWriteCallbacks
): void => {
	optimisticCounter += 1;
	const placeholderId = `client:new-schedule:${optimisticCounter}`;
	commit(
		optimisticMutation(createMutation, store => {
			const setRecord = store.get(set.id);
			if (!setRecord) {
				return;
			}
			const row = store.create(placeholderId, 'Schedule');
			row.setValue(placeholderId, 'id');
			row.setValue(placeholderId, 'scheduleId');
			row.setValue(fields.frequency, 'frequency');
			row.setValue(fields.dayOfMonth, 'dayOfMonth');
			row.setValue([...fields.weekdays], 'weekdays');
			row.setValue(fields.timeOfDay, 'timeOfDay');
			row.setValue(fields.timezone, 'timezone');
			setRecord.setLinkedRecords([...(setRecord.getLinkedRecords('schedules') ?? []), row], 'schedules');
		}),
		{consumer: set.consumer, ...fields},
		callbacks,
		false
	);
};

export const commitUpdateSchedule = (
	set: ScheduleSetSnapshot,
	scheduleId: string,
	change: ScheduleChange,
	callbacks: ScheduleWriteCallbacks
): void => {
	const recordId = set.schedules.find(schedule => schedule.scheduleId === scheduleId)?.id;
	commit(
		optimisticMutation(updateMutation, store => {
			const row = recordId ? store.get(recordId) : null;
			if (!row) {
				return;
			}
			Object.entries(change).forEach(([field, value]) => {
				row.setValue(Array.isArray(value) ? [...value] : (value as string | null), field);
			});
		}),
		{consumer: set.consumer, scheduleId, ...change},
		callbacks,
		true
	);
};

export const commitDeleteSchedule = (set: ScheduleSetSnapshot, scheduleId: string, callbacks: ScheduleWriteCallbacks): void => {
	const recordId = set.schedules.find(schedule => schedule.scheduleId === scheduleId)?.id;
	commit(
		optimisticMutation(deleteMutation, store => {
			const setRecord = store.get(set.id);
			if (!setRecord || !recordId) {
				return;
			}
			setRecord.setLinkedRecords(
				(setRecord.getLinkedRecords('schedules') ?? []).filter(row => row?.getDataID() !== recordId),
				'schedules'
			);
		}),
		{consumer: set.consumer, scheduleId},
		callbacks,
		true
	);
};
